import {
  Fragment,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import type { MissionsCampaign } from "@/campaigns/modules/missions/types";
import {
  allMissionsCompleted,
  completedMissionCount,
  currentMission,
} from "@/campaigns/modules/missions/types";
import {
  MissionCard,
  MissionDetailCard,
  MissionNavCard,
  type MissionCardState,
} from "./mission-card";
import {
  MissionSelectionContext,
  useMissionSelection,
} from "./mission-selection";
import { StatFigure } from "@/components/campaigns/campaign-shell/campaign-sections";
import { Card } from "@/components/ui/card";
import { CampaignCompletedBanner } from "@/components/campaigns/campaign-completed-banner";
import { cn } from "@/lib/utils";

/** Sidebar copy for the shared HowToWinCard (English default; translated via
 *  `missions.howItWorks` where it's rendered). Deliberately never says
 *  "mission" — partners brand the feature with their own word. */
export const MISSIONS_HOW_IT_WORKS =
  "Complete the tasks to claim the reward. Each one you finish unlocks the next.";

/** Sidebar stat block — the missions equivalent of "Remaining prize drops". */
export const MissionsCompletedCard = ({
  missionsCampaign,
  density = "default",
  children,
}: {
  missionsCampaign: MissionsCampaign;
  /** See `StatFigure`. */
  density?: "default" | "dock";
  /** Under the caption: the overlay's dock panel puts the join state here. */
  children?: ReactNode;
}) => {
  const { t } = useTranslation();
  const done = completedMissionCount(missionsCampaign);
  const total = missionsCampaign.missions.length;
  const finishedAll = allMissionsCompleted(missionsCampaign);
  return (
    <Card
      title={t("missions.completedStat", "Completed")}
      className="flex flex-col items-start gap-4"
      // The stack's 16px gap minus 4: the same 12px under the title as every other card.
      titleRowClassName="-mb-1 w-full"
    >
      <div className="flex h-10.5 w-full flex-col items-start justify-center overflow-hidden">
        <StatFigure density={density}>
          {t("missions.completedOf", "{{done}} of {{total}}", { done, total })}
        </StatFigure>
      </div>
      <div className="h-px w-full bg-foreground/6" />
      <p className="font-body text-[13px] leading-[18px] text-muted-foreground">
        {finishedAll
          ? t(
              "missions.completedCaptionDone",
              "Every reward claimed — nice work"
            )
          : t(
              "missions.completedCaption",
              "Complete all to claim every reward"
            )}
      </p>
      {children}
    </Card>
  );
};

/** Celebration banner atop the ladder once the player finishes every mission.
 *  The subtitle stays generic: prize titles are pre-rendered strings, so the
 *  widget cannot sum amounts into an "earned €X" line. The surface is the shared
 *  `CampaignCompletedBanner`; only the copy is this type's. */
export const MissionsFinishedBanner = () => {
  const { t } = useTranslation();
  return (
    <CampaignCompletedBanner
      title={t("missions.allCompletedTitle", "Everything completed!")}
      subtitle={t(
        "missions.allCompletedSubtitle",
        "You've claimed every reward in this campaign."
      )}
    />
  );
};

/** The scroller an element lives in, if any (the shell's panes scroll inside themselves). */
const nearestScroller = (el: HTMLElement): HTMLElement | null => {
  let scroller = el.parentElement;
  while (
    scroller &&
    !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)
  )
    scroller = scroller.parentElement;
  return scroller;
};

/** Each step's rung state: every `is_active` mission is open — the backend promises exactly one,
 *  but if it ever flags several, showing them dimmed-locked with a wrong unlock hint would be
 *  worse than showing two open rungs. `current` covers the no-live-state fallback (first
 *  uncompleted mission). */
const useStepStates = (mc: MissionsCampaign) => {
  const current = currentMission(mc);
  return (m: MissionsCampaign["missions"][number]): MissionCardState =>
    m.isCompleted
      ? "completed"
      : m.isActive || m === current
        ? "active"
        : "locked";
};

const tabId = (campaignId: number, missionId: number) =>
  `mission-tab-${campaignId}-${missionId}`;
const panelId = (campaignId: number) => `mission-panel-${campaignId}`;

/** The ladder as a vertical tab list (landscape): which step is selected, and how to pick one. */
type MissionNav = {
  selectedId: number | null;
  onSelect: (id: number) => void;
};

/**
 * Primary tab — the ordered mission ladder (Figma 5517:778). With `nav`, the same ladder as the
 * landscape NAVIGATION column: every step a compact tab card (`MissionNavCard`) instead of a rung
 * that folds open, the selected step's tasks showing in the detail pane beside it. At `dock`
 * density (the overlay's column) the rungs are at their dock density, and the all-done banner is
 * left to the completed card the dock panel leads with.
 */
export const MissionLadder = ({
  missionsCampaign,
  nav,
  density,
}: {
  missionsCampaign: MissionsCampaign;
  nav?: MissionNav;
  density?: "default" | "dock";
}) => {
  const { t } = useTranslation();
  const finishedAll = allMissionsCompleted(missionsCampaign);
  const stateOf = useStepStates(missionsCampaign);
  const campaignId = missionsCampaign.campaign.id;
  const missions = missionsCampaign.missions;

  // The navigation column opens scrolled to the selected step — once, when it mounts (height is
  // scarce sideways, so the player's step would otherwise sit below the fold); not on every
  // pick, which would yank a card the player just tapped. Only the pane moves — never the host
  // page (scrollIntoView would scroll the game page around the widget too).
  const selectedRef = useRef<HTMLDivElement>(null);
  const scrolledOnce = useRef(false);
  useLayoutEffect(() => {
    const step = selectedRef.current;
    if (!nav || !step || scrolledOnce.current) return;
    scrolledOnce.current = true;
    const scroller = nearestScroller(step);
    if (!scroller) return;
    const offset =
      step.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTop +=
      offset - (scroller.clientHeight - step.offsetHeight) / 2;
  }, [nav]);

  // Vertical tab list keyboard pattern: the arrows move the selection, Home/End jump.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!nav || missions.length === 0) return;
    const index = missions.findIndex((m) => m.id === nav.selectedId);
    const next =
      e.key === "ArrowDown"
        ? Math.min(missions.length - 1, index + 1)
        : e.key === "ArrowUp"
          ? Math.max(0, index - 1)
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? missions.length - 1
              : -1;
    if (next < 0 || next === index) return;
    e.preventDefault();
    nav.onSelect(missions[next].id);
    e.currentTarget
      .querySelector<HTMLElement>(`#${tabId(campaignId, missions[next].id)}`)
      ?.focus();
  };

  if (missions.length === 0) {
    return (
      <Card className="flex items-center justify-center py-8">
        <p className="font-body text-[12px] text-muted-foreground lg:text-[14px]">
          {t("missions.emptyLadder", "Nothing here yet — check back soon")}
        </p>
      </Card>
    );
  }

  return (
    // One path, top to bottom: each step's bead straddles its card's top edge, and a short run of
    // dots leads from one card to the next bead, coloured by where the player is (`StepPath`).
    // On desktop the ladder holds a reading measure, centred in the column: stretched across the
    // whole column the rungs went flat and wide. The landscape navigation column is narrower than
    // that anyway.
    <div className="mx-auto flex w-full flex-col items-center lg:max-w-[560px]">
      {finishedAll && !nav && density !== "dock" && (
        <div className="mb-3 w-full">
          <MissionsFinishedBanner />
        </div>
      )}
      <div
        className="flex w-full flex-col items-center"
        role={nav ? "tablist" : undefined}
        aria-orientation={nav ? "vertical" : undefined}
        onKeyDown={nav ? onKeyDown : undefined}
      >
        {missions.map((mission, index) => {
          const state = stateOf(mission);
          const selected = nav != null && mission.id === nav.selectedId;
          return (
            <Fragment key={mission.id}>
              {index > 0 && (
                <StepPath
                  from={stateOf(missions[index - 1])}
                  to={state}
                  short={nav != null}
                />
              )}
              {/* Each card sits its bead's overhang below what comes before it (18px for the
                  active step's bigger bead, 14px otherwise), so the path's dots fill exactly
                  the visible gap between one card and the next bead. */}
              <div
                ref={selected ? selectedRef : undefined}
                className={cn(
                  "w-full",
                  state === "active" ? "mt-[18px]" : "mt-3.5"
                )}
              >
                {nav ? (
                  <MissionNavCard
                    mission={mission}
                    state={state}
                    stepTotal={missions.length}
                    selected={selected}
                    onSelect={() => nav.onSelect(mission.id)}
                    id={tabId(campaignId, mission.id)}
                    controls={panelId(campaignId)}
                  />
                ) : (
                  <MissionCard
                    mission={mission}
                    state={state}
                    stepTotal={missions.length}
                    density={density}
                  />
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Which step the player is looking at on a phone held sideways, where the ladder splits into a
 * navigation column and a detail pane: the two panes are separate scrollers in the shell, so the
 * choice lives above both (mounted through `CampaignView.landscape.frame`). `null` = nothing
 * picked yet, and `useMissionSelection` falls back to the step the player is on.
 */
export const MissionSelectionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [selectedId, select] = useState<number | null>(null);
  const value = useMemo(() => ({ selectedId, select }), [selectedId]);
  return (
    <MissionSelectionContext.Provider value={value}>
      {children}
    </MissionSelectionContext.Provider>
  );
};

/**
 * The landscape Overview's NARROW pane: the ladder as navigation. Which step is open is shared
 * with `MissionsLandscapeDetail` through `MissionSelectionProvider` (mounted by the module's
 * `landscape.frame`).
 */
export const MissionsLandscapeNav = ({
  missionsCampaign,
}: {
  missionsCampaign: MissionsCampaign;
}) => {
  const { mission, select } = useMissionSelection(missionsCampaign);
  return (
    <MissionLadder
      missionsCampaign={missionsCampaign}
      nav={{ selectedId: mission?.id ?? null, onSelect: select }}
    />
  );
};

/**
 * The landscape Overview's WIDE pane: the selected step's reward and tasks (`MissionDetailCard`),
 * under the all-done banner once every step is complete. A new pick starts at the top of the
 * pane: the previous step's scroll position means nothing for this one.
 */
export const MissionsLandscapeDetail = ({
  missionsCampaign,
}: {
  missionsCampaign: MissionsCampaign;
}) => {
  const { mission } = useMissionSelection(missionsCampaign);
  const stateOf = useStepStates(missionsCampaign);
  const campaignId = missionsCampaign.campaign.id;
  const ref = useRef<HTMLDivElement>(null);
  const missionId = mission?.id;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const scroller = nearestScroller(el);
    if (scroller) scroller.scrollTop = 0;
  }, [missionId]);
  if (!mission) return null;
  return (
    <div ref={ref} className="flex w-full flex-col gap-2">
      {allMissionsCompleted(missionsCampaign) && <MissionsFinishedBanner />}
      <MissionDetailCard
        key={mission.id}
        mission={mission}
        state={stateOf(mission)}
        id={panelId(campaignId)}
        labelledBy={tabId(campaignId, mission.id)}
      />
    </div>
  );
};

/** How many dots a stretch of the path into a locked step has. */
const PATH_DOTS = 3;

/**
 * A stretch of the path between two steps (`.step-path`, index.css), coloured along a gradient
 * from the earlier step's state colour to the later one's — gold for a finished step, the progress
 * colour for the active one, a dim grey for one ahead — at a muted strength, so the path guides
 * without competing with the cards. A thin line where the path is already open (into a finished or
 * the active step); a few round dots where it leads to a locked step — the road not yet taken.
 * Half as long in the landscape navigation column. Static and decorative: the beads carry the
 * state for screen readers.
 */
const StepPath = ({
  from,
  to,
  short = false,
}: {
  from: MissionCardState;
  to: MissionCardState;
  /** Half the run: the landscape navigation column, where height is scarce. */
  short?: boolean;
}) => (
  <span
    aria-hidden="true"
    className={cn(
      "step-path",
      short && "step-path-short",
      `step-path-from-${from}`,
      `step-path-to-${to}`
    )}
  >
    {to === "locked" ? (
      Array.from({ length: PATH_DOTS }, (_, i) => (
        <span
          key={i}
          className="step-path-dot"
          style={{ "--p": `${(i / (PATH_DOTS - 1)) * 100}%` } as CSSProperties}
        />
      ))
    ) : (
      <span className="step-path-line" />
    )}
  </span>
);
